// src/App.js
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useAppTheme } from './contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Theme
import createAppTheme from './utils/theme';

// Services
import AuthService from './services/authService';

// Brand Config
import { BRAND_CONFIG, getLogoSrc } from './config/brandConfig';

// Context
import { CompanyProvider } from './contexts/CompanyContext';
import { applySsoFromHash } from './utils/ssoHandoff';

// Layout
import MainLayout from './components/common/Layout/MainLayout';

import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import Profile from './pages/Profile/Profile';
import ChangePassword from './pages/Profile/ChangePassword';
import EdefterUpload from './pages/Edefter/EdefterUpload';
import GeneralJournal from './pages/Edefter/GeneralJournal';
import TrialBalance from './pages/Edefter/TrialBalance';
import MizanReading from './pages/Edefter/MizanReading';
import AddCompany from './pages/Edefter/AddCompany';
import EDashboard from './pages/Edefter/EDashboard';

// Apply inbound SSO before React mounts (tahsis → fidost company + auth handoff)
applySsoFromHash();

// Protected Route Component - Redirects unauthenticated users to login
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component - Only allows Admin users
const AdminRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  const currentUser = AuthService.getCurrentUser();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (currentUser?.role !== 'Admin') {
    return <Navigate to="/profile" replace />;
  }
  
  return children;
};

// Auth Route Component (authenticated users should not access login/register/forgot-password)
const AuthRoute = ({ children }) => {
  const isAuthenticated = AuthService.isAuthenticated();
  
  if (isAuthenticated) {
    return <Navigate to="/edefter/trial-balance" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const { darkMode } = useAppTheme();
  const appTheme = useMemo(
    () => createAppTheme(user?.role || 'User', darkMode),
    [user?.role, darkMode]
  );

  // Update user information
  const updateUser = () => {
    const currentUser = AuthService.getCurrentUser();
    setUser(currentUser || {
      name: 'John Doe',
      email: 'john@example.com',
      role: 'User', // Default role is now User
      userId: 1
    });
  };

  // Global toast function
  useEffect(() => {
    window.showToast = (message, type = 'info') => {
      const toastOptions = {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      };

      switch (type) {
        case 'success':
          toast.success(message, toastOptions);
          break;
        case 'error':
          toast.error(message, toastOptions);
          break;
        case 'warning':
          toast.warning(message, toastOptions);
          break;
        default:
          toast.info(message, toastOptions);
      }
    };

    return () => {
      delete window.showToast;
    };
  }, []);

  // Update document title and favicon based on brand config
  useEffect(() => {
    // Update document title
    document.title = BRAND_CONFIG.appName;
    
    // Update favicon dynamically
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = BRAND_CONFIG.favicon;
    link.type = BRAND_CONFIG.favicon.endsWith('.ico') ? 'image/x-icon' : 'image/png';
    
    // Update apple-touch-icon
    let appleLink = document.querySelector("link[rel*='apple-touch-icon']");
    if (!appleLink) {
      appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      document.getElementsByTagName('head')[0].appendChild(appleLink);
    }
    appleLink.href = BRAND_CONFIG.favicon;
  }, []);

  // Get user information on initial load and listen to login/logout events
  useEffect(() => {
    updateUser();

    // Listen to login event
    const handleUserLogin = () => {
      updateUser();
    };

    // Listen to logout event
    const handleUserLogout = () => {
      setUser(null);
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('userLogout', handleUserLogout);

    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('userLogout', handleUserLogout);
    };
  }, []);

  // Session timeout check
  useEffect(() => {
    // Session check every 5 minutes - token is checked every 5 minutes, if it expires, authService.logout() is called.
    const interval = setInterval(() => {
      if (AuthService.isAuthenticated()) {
        AuthService.checkSessionTimeout();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  // Show loading until user information is loaded
  if (user === null) {
    return (
      <MuiThemeProvider theme={appTheme}>
        <CssBaseline />
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#F2F2F7] dark:bg-black font-sans">
          <div className="w-14 h-14 rounded-2xl bg-[#24299d] flex items-center justify-center mb-6 animate-pulse shadow-lg shadow-[#24299d]/30">
            <img
              src={getLogoSrc(darkMode)}
              alt=""
              className="h-10 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="w-10 h-10 border-4 border-[#24299d]/20 border-t-[#24299d] rounded-full animate-spin mb-4" />
          <p className="text-lg font-semibold text-gray-900 dark:text-white">{BRAND_CONFIG.appName}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Loading...</p>
        </div>
      </MuiThemeProvider>
    );
  }

  return (
    <MuiThemeProvider theme={appTheme}>
      <CssBaseline />
      <CompanyProvider>
        <Router>
          <Routes>
          {/* Auth Routes */}
          <Route 
            path="/login" 
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <AuthRoute>
                <Register />
              </AuthRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={
              <AuthRoute>
                <ForgotPassword />
              </AuthRoute>
            } 
          />

          {/* Protected Routes */}

          {/* Fidost dashboard (not tahsis redirect) */}
          <Route
            path="/edefter/dashboard"
            element={
              <ProtectedRoute>
                <MainLayout user={user || { name: 'User', role: 'User', email: 'user@example.com' }}>
                  <EDashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/edefter/upload" 
            element={
              <AdminRoute>
                <MainLayout user={user || { name: 'Admin', role: 'Admin', email: 'admin@example.com' }}>
                  <EdefterUpload />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/general-journal" 
            element={
              <AdminRoute>
                <MainLayout user={user || { name: 'Admin', role: 'Admin', email: 'admin@example.com' }}>
                  <GeneralJournal />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/trial-balance" 
            element={
              <AdminRoute>
                <MainLayout
                  user={user || { name: 'Admin', role: 'Admin', email: 'admin@example.com' }}
                  contentMaxWidth="max-w-7xl"
                >
                  <TrialBalance />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/mizan-reading" 
            element={
              <AdminRoute>
                <MainLayout user={user || { name: 'Admin', role: 'Admin', email: 'admin@example.com' }}>
                  <MizanReading />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/edefter/add-company" 
            element={
              <AdminRoute>
                <MainLayout user={user || { name: 'Admin', role: 'Admin', email: 'admin@example.com' }}>
                  <AddCompany />
                </MainLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || { name: 'User', role: 'User', email: 'user@example.com' }}>
                  <Profile user={user || { name: 'User', role: 'User', email: 'user@example.com' }} />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <MainLayout user={user || { name: 'User', role: 'User', email: 'user@example.com' }}>
                  <ChangePassword />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          

          {/* Admin Routes */}
         
          {/* Default Route */}
          <Route path="/" element={<Navigate to="/edefter/trial-balance" replace />} />

          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/edefter/trial-balance" replace />} />
        </Routes>
        </Router>
      </CompanyProvider>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={darkMode ? 'dark' : 'light'}
      />
    </MuiThemeProvider>
  );
}

export default App;